import React, { useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Typography } from '@mui/material'
import awsImage from '../../images/awsCloud.png'
import gcpImage from '../../images/gcpMarketPlace.png'
import azureImage from '../../images/azureCloud.png'
import {
  faCircle,
  faFlag,
  faSquare,
  faTriangle,
  faTrophy,
} from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ButtonV2, MainTabsV2, SidePanelInfo } from '@labrav/react-components'
import { SaasProducts } from './SaasProducts/SaasProducts'
import { useUserType } from '../../utils/hooks/useUserType'
import { getPartnerIdFromState } from '../../utils/hooks/partnerStateSelectors'
import {
  getMilestonesApiData,
  type MilestonesDispatch,
} from './MilestonesAction/MilestonesAction'
import { useTracker } from '../../utils/Tracker/hook'
import { useStyle } from './Milestones.styles'

type MilestonesState = {
  flyoutSellerInfo: {
    awsAccountName?: string
  }
  AccountData?: {
    user?: {
      partnerData?: {
        isInternal?: boolean
      } | null
    }
    admin?: {
      partnerData?: {
        isInternal?: boolean
      } | null
    }
  }
}

const pageNameFromPath = (pathname: string) => {
  if (pathname.includes('products-listing')) {
    return 'Product listings'
  } else if (pathname.includes('overview')) {
    return 'Overview'
  } else if (pathname.includes('private-offers')) {
    return 'Private offers & Reseller authorizations'
  } else if (pathname.includes('revenue')) {
    return 'Revenue insights'
  } else if (pathname.includes('subscriptions')) {
    return 'Subscriptions'
  } else {
    return ''
  }
}

export const Milestones: React.FC = () => {
  const userType = useUserType()
  const { pathname } = useLocation()
  const accountId =
    useSelector((state: MilestonesState) =>
      getPartnerIdFromState(state, pathname, userType)
    ) || ''
  const dispatch = useDispatch<MilestonesDispatch>()
  const tracker = useTracker()
  const isInternal = useSelector((state: MilestonesState) => {
    return state.AccountData?.[userType]?.partnerData?.isInternal || false
  })
  const sellerName = useSelector(
    (state: MilestonesState) => state.flyoutSellerInfo.awsAccountName
  )
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [isMilestonesButtonClicked, setIsMilestonesButtonClicked] =
    useState<boolean>(false)
  const [saasProductsData, setSaasProductsData] = useState<unknown>(null)
  const milestoneData = saasProductsData as {
    newCount?: number
    completedCount?: number
    totalCount?: number
    milestones?: Record<string, boolean>
  } | null
  const awsTabCount = milestoneData?.newCount || 0
  const pageName = pageNameFromPath(pathname)
  const { classes } = useStyle({ pageName })

  const openDrawer = useCallback(async () => {
    await setIsOpen(true)
    await setIsMilestonesButtonClicked(true)
    await dispatch(getMilestonesApiData(accountId, setSaasProductsData))
    if (!isInternal) {
      tracker?.track({
        product: 'FLYOUT',
        section: 'milestones',
        component: 'milestones-button',
        action: 'clicked',
        title: 'Milestones viewed',
        source: `${pageName} Page`,
        partnerId: `${accountId}`,
        sellerName: `${sellerName}`,
      })
    }
  }, [accountId])

  const toggleDrawer = () => {
    setIsOpen(false)
  }

  const handleCloudTabChange = (index: number) => {
    setActiveTabIndex(index)
  }

  const cloudTabs = useMemo(
    () => [
      {
        label: 'AWS',
        key: 'aws',
        icon: (
          <img src={awsImage} alt="aws-image" className={classes.awsImage} />
        ),
        renderElement: () => (
          <CloudTabContent
            partnerId={accountId}
            setSaasProductsData={setSaasProductsData}
            completedCount={milestoneData?.completedCount || 0}
            totalCount={milestoneData?.totalCount || 0}
            saasProductsMiestonesData={milestoneData?.milestones || {}}
          />
        ),
        ...(awsTabCount > 0 ? { count: awsTabCount } : {}),
      },
      {
        label: 'GCP',
        key: 'gcp',
        icon: (
          <img src={gcpImage} alt="gcp-image" className={classes.cloudImage} />
        ),
        renderElement: () => null,
        disabled: true,
        tootTipText: 'Coming soon',
      },
      {
        label: 'Microsoft',
        key: 'azure',
        icon: (
          <img
            src={azureImage}
            alt="azure-image"
            className={classes.cloudImage}
          />
        ),
        renderElement: () => null,
        disabled: true,
        tootTipText: 'Coming soon',
      },
    ],
    [
      accountId,
      awsTabCount,
      saasProductsData,
      classes.awsImage,
      classes.cloudImage,
    ]
  )
  const selectedContent = cloudTabs?.find(
    (tabContent, index) => index === activeTabIndex
  )?.renderElement
  return (
    <>
      <div className={classes.root} data-testid="milestones-container">
        <ButtonV2
          key={'milestones-button'}
          styleType="link"
          data-testid="milestones-button"
          icon={
            <div
              className={classes.iconContainer}
              data-testid="milestones-icon-container"
            >
              <FontAwesomeIcon icon={faFlag} className={classes.buttonIcon} />
              {!isMilestonesButtonClicked && (
                <div className={classes.dotContainer}></div>
              )}
            </div>
          }
          onClick={openDrawer}
        >
          {pageName === 'Overview' && (
            <Typography className={classes.text} data-testid="milestones-text">
              Milestones
            </Typography>
          )}
        </ButtonV2>
      </div>
      <div className={classes.sidePanelInfoContainer}>
        <SidePanelInfo
          icon={
            <div className={classes.sidePanelIcon}>
              <FontAwesomeIcon
                icon={faTrophy}
                className={classes.sidePanelInfoHeaderIcon}
              />
            </div>
          }
          variant={'secondary'}
          headerTitle={
            <Typography
              className={classes.sidePanelHeaderText}
              data-testid="milestones-header-title"
            >
              Milestones
            </Typography>
          }
          subHeaderContent={
            <div
              className={classes.mainTabContainer}
              data-testid="milestones-sub-header-content"
            >
              <MainTabsV2
                activeTabIndex={activeTabIndex}
                onChange={handleCloudTabChange}
                tabs={cloudTabs}
              />
            </div>
          }
          open={isOpen}
          onClose={toggleDrawer}
          content={
            <div className={classes.mainTabContent}>
              {selectedContent && selectedContent()}
            </div>
          }
        />
      </div>
    </>
  )
}

interface CloudTabContentProps {
  partnerId: string
  setSaasProductsData: React.Dispatch<React.SetStateAction<unknown>>
  completedCount: number | string
  totalCount: number | string
  saasProductsMiestonesData: Record<string, boolean>
}

const CloudTabContent: React.FC<CloudTabContentProps> = ({
  partnerId: accountId,
  setSaasProductsData,
  completedCount,
  totalCount,
  saasProductsMiestonesData,
}) => {
  const { classes } = useStyle({})
  const [activeTabIndex, setActiveTabIndex] = useState(0)

  const handleProductTypeTabChange = (index: number) => {
    setActiveTabIndex(index)
  }

  const productTypeTabs = [
    {
      label: 'SaaS products',
      key: 'saas-products',
      icon: (
        <FontAwesomeIcon icon={faTriangle} className={classes.innerTabIcon} />
      ),
      renderElement: (
        <SaasProducts
          partnerId={accountId}
          setSaasProductsData={setSaasProductsData}
          completedCount={completedCount}
          totalCount={totalCount}
          saasProductsMiestonesData={saasProductsMiestonesData}
        />
      ),
    },
    {
      label: 'Container',
      key: 'container',
      icon: (
        <FontAwesomeIcon icon={faCircle} className={classes.innerTabIcon} />
      ),
      disabled: true,
      tootTipText: 'Coming soon',
    },
    {
      label: 'Professional service',
      key: 'professional-service',
      icon: (
        <FontAwesomeIcon icon={faSquare} className={classes.innerTabIcon} />
      ),
      disabled: true,
      tootTipText: 'Coming soon',
    },
  ]
  const selectedContent = productTypeTabs?.find(
    (tabContent, index) => index === activeTabIndex
  )?.renderElement
  return (
    <>
      <div data-testid="content">
        <MainTabsV2
          activeTabIndex={activeTabIndex}
          onChange={handleProductTypeTabChange}
          tabs={productTypeTabs}
          variant={'secondaryTab'}
        />
        <div className={classes.cloudTabContent}>{selectedContent}</div>
      </div>
    </>
  )
}
